import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { API_BASE } from "@/config/links";
import { LANDING } from "@/constants/testIds";
import { trackClick } from "@/lib/track";
import { fadeUp, hoverLift, revealProps, stagger } from "@/lib/motion";

// Цены — дефолт на случай недоступного API; живые значения приходят из /api/products
// (те же продукты, что правятся в админке и показывает бот).
const PLANS = [
  { slug: "free-story", code: "free_story", title: "Первая сказка", priceRub: 0, note: "После входа по почте", cta: "Сделать бесплатно" },
  {
    slug: "story-situation",
    code: "story_situation_audio_399",
    title: "Про то, что волнует",
    priceRub: 399,
    note: "садик, темнота, зубы, новый братик",
    cta: "Заказать",
  },
  {
    slug: "story-hero",
    code: "story_hero_audio_599",
    title: "Ребёнок — главный герой",
    priceRub: 599,
    note: "ваш ребёнок в центре сюжета",
    cta: "Заказать",
    featured: true,
  },
  { slug: "story-mom", code: "story_mom_audio_399", title: "Сказка для мамы", priceRub: 399, note: "пять тихих минут", cta: "Заказать" },
];

function formatPrice(value) {
  return `${Number(value || 0).toLocaleString("ru-RU")} ₽`;
}

const Pricing = () => {
  const [plans, setPlans] = useState(PLANS);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/products`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data?.products?.length) return;
        const byCode = new Map(data.products.map((p) => [p.code, p]));
        // Берём только цену: названия на лендинге свои, в админке они техничнее.
        setPlans(PLANS.map((plan) => {
          const live = byCode.get(plan.code);
          return live ? { ...plan, priceRub: live.priceRub } : plan;
        }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section id="pricing" className="relative overflow-hidden bg-night-900 py-16 md:py-24">
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div {...revealProps} variants={stagger(0.08)} className="mb-12 text-center">
          <motion.p variants={fadeUp} className="mb-3 text-[11px] uppercase tracking-[0.22em] text-amber">
            Цены
          </motion.p>
          <motion.h2 variants={fadeUp} className="mb-4 font-serif text-3xl text-cream sm:text-4xl lg:text-5xl">
            Сколько стоит
          </motion.h2>
          <motion.p variants={fadeUp} className="mx-auto max-w-lg leading-relaxed text-muted-foreground">
            Первая сказка бесплатно. Каждая платная — текст, озвучка, картинка и видео
          </motion.p>
        </motion.div>

        <motion.div
          {...revealProps}
          variants={stagger(0.09)}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5"
        >
          {plans.map((plan) => (
            <motion.div
              key={plan.slug}
              variants={fadeUp}
              whileHover={hoverLift}
              data-testid={`${LANDING.pricingCardPrefix}${plan.slug}`}
              className={`relative flex flex-col rounded-3xl border p-6 backdrop-blur-xl transition-colors duration-300 ${
                plan.featured
                  ? "border-amber/50 bg-gradient-to-b from-amber/[0.14] via-white/[0.05] to-transparent shadow-glow-lg lg:-mt-4 lg:pb-9 lg:pt-9"
                  : "border-line bg-white/[0.04] hover:border-amber/35"
              }`}
            >
              {plan.featured ? (
                <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-amber px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-night-900">
                  <Sparkles className="h-3 w-3" /> Популярно
                </span>
              ) : null}
              <h3 className="mb-2 mt-1 font-serif text-xl leading-snug text-cream">{plan.title}</h3>
              <p className="mb-1 text-3xl font-semibold text-amber">{formatPrice(plan.priceRub)}</p>
              <p className="mb-6 text-xs text-dim">{plan.note}</p>
              <Link
                data-testid={`${LANDING.pricingCardCtaPrefix}${plan.slug}`}
                to="/chat"
                onClick={() => trackClick("chat")}
                className={`mt-auto inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full px-4 text-sm font-semibold transition-all duration-300 ${
                  plan.featured
                    ? "bg-amber text-night-900 shadow-glow hover:bg-amber-hover hover:shadow-glow-lg"
                    : "border border-line-strong bg-white/[0.03] text-cream hover:border-amber/60 hover:text-amber-soft"
                }`}
              >
                {plan.cta}
                <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
